import { useEffect, useState } from "react";
import { Button, IconButton } from "@/components/ui/Button";
import { Icon } from "@/components/ui/Icon";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const DISMISS_KEY = "flow.installPrompt.dismissed";

function isIosSafari(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const iOS = /iPad|iPhone|iPod/.test(ua);
  const webkit = /WebKit/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  const inStandalone =
    "standalone" in navigator && (navigator as Navigator & { standalone?: boolean }).standalone;
  return iOS && webkit && !inStandalone;
}

export function InstallPrompt() {
  const [deferredEvent, setDeferredEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIos, setShowIos] = useState<boolean>(false);
  const [dismissed, setDismissed] = useState<boolean>(() =>
    typeof window !== "undefined" ? localStorage.getItem(DISMISS_KEY) === "1" : false,
  );

  useEffect(() => {
    if (dismissed) return;
    const onBeforeInstall = (event: Event) => {
      event.preventDefault();
      setDeferredEvent(event as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    if (isIosSafari()) setShowIos(true);
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, [dismissed]);

  if (dismissed) return null;
  if (!deferredEvent && !showIos) return null;

  const close = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  return (
    <div
      role="dialog"
      aria-label="Flow installieren"
      className="safe-bottom safe-x"
      style={{
        position: "fixed",
        left: 12,
        right: 12,
        bottom: 12,
        margin: "0 auto",
        maxWidth: 480,
        padding: 12,
        background: "var(--surface)",
        color: "var(--ink-1)",
        border: "1px solid var(--hairline)",
        borderRadius: 12,
        boxShadow: "0 12px 32px rgba(13,62,99,0.18)",
        zIndex: 40,
        fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <div
          style={{
            display: "flex",
            width: 40,
            height: 40,
            flexShrink: 0,
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 8,
            background: "var(--surface-soft)",
            color: "var(--ink-2)",
          }}
        >
          <Icon name="upload" size={20} />
        </div>
        <div style={{ flex: 1, fontSize: 13.5 }}>
          <p style={{ margin: 0, fontWeight: 600 }}>Flow installieren</p>
          <p style={{ margin: "4px 0 0", color: "var(--ink-3)" }}>
            {showIos
              ? "Tippe auf das Teilen-Symbol in Safari und wähle „Zum Home-Bildschirm“."
              : "Füge Flow zum Home-Bildschirm hinzu für schnellen Zugriff."}
          </p>
        </div>
        <IconButton icon="x" size={32} onClick={close} aria-label="Schließen" />
      </div>
      {deferredEvent ? (
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
          <Button
            variant="primary"
            size="sm"
            onClick={async () => {
              await deferredEvent.prompt();
              await deferredEvent.userChoice;
              close();
            }}
          >
            Installieren
          </Button>
        </div>
      ) : null}
    </div>
  );
}
